import { ApolloError } from '@apollo/client';
import React, { useEffect, useState } from 'react';

export const TWO_FACTOR_CODE_USED = 'TWO_FACTOR_CODE_USED';

// when the API refused a code because it was already used: how long until authenticator apps show a new one
export const getNewCodeInSeconds = (error: unknown) => {
  if (!(error instanceof ApolloError)) return null;
  const usedCodeError = error.graphQLErrors.find(
    ({ extensions }) => extensions?.code === TWO_FACTOR_CODE_USED,
  );
  const seconds = usedCodeError?.extensions?.retryAfterSeconds;
  return typeof seconds === 'number' ? seconds : null;
};

const secondsUntil = (time: number) => Math.max(0, Math.ceil((time - Date.now()) / 1000));

interface Props {
  newCodeInSeconds: number;
}

// counts down, from when the API answered, to when the authenticator app shows a new code
export default function TwoFactorCodeUsedMessage({ newCodeInSeconds }: Props) {
  const [newCodeAt] = useState(() => Date.now() + newCodeInSeconds * 1000);
  const [secondsLeft, setSecondsLeft] = useState(() => secondsUntil(newCodeAt));

  useEffect(() => {
    const interval = setInterval(() => {
      const left = secondsUntil(newCodeAt);
      setSecondsLeft(left);
      if (left === 0) clearInterval(interval);
    }, 250);
    return () => clearInterval(interval);
  }, [newCodeAt]);

  return (
    <>
      That code was already used. {/* read out once when the message appears, not on every tick */}
      <span aria-live="off">
        {secondsLeft > 0
          ? `Please try again with a new code in ${secondsLeft}s.`
          : 'Please try again with the new code from your authenticator app.'}
      </span>
    </>
  );
}
